import Vue from 'vue'
import Vuex from 'vuex'

import {
  PepperClient,
  UploadTask,
  DownloadTask,
  ThumbsTask,
} from './libpepper.js'

const pwdata_file_path = '/.mods/password-manager.json';
const notes_file_path = '/.mods/notes.json';
const ingress_file_path = '/.mods/ingress.json';
const favs_file_path = '/.mods/favs.json';

Vue.use(Vuex);

function refresh_userinfo(client) {
  return client.refresh_userinfo().catch(e => {
    if (e.response.status == 401) {
      client.token = null;
      localStorage.removeItem("token");
      window.location.reload(false);
    }
  });
}

function create_client() {
  var client = new PepperClient(process.env.VUE_APP_PEPPER_API_SERVER);

  client.api_error_cb = (e) => {
    Vue.$gtag.event('api-error', {
      event_category: 'error',
      event_label: e.statusText,
      value: e.status,
    });
    if (e.response && e.response.status == 401) {
      // 401 means we are logged out by the server, verify that in a bit
      setTimeout(() => {
        refresh_userinfo(client);
      }, 1000);
    }
  };

  var token = localStorage.getItem("token");
  if (token) {
    client.token = token;
    refresh_userinfo(client);
  }

  return client;
}

function total_percent(tasks) {
  var total = 0;
  var progress = 0;
  var unfinished = false;
  tasks.forEach(t => {
    if (t.percent_skip) {
      return;
    }
    total += t.total;
    progress += t.progress;
    if (!t.finished) {
      unfinished = true;
    }
  });
  var ret = total > 0 ? Math.ceil(progress * 100 / total) : 0;
  if (unfinished) {
    ret = Math.min(99, ret);
  } else {
    tasks.forEach(t => {
      t.percent_skip = true;
    });
  }
  return isNaN(ret) ? 0 : ret;
}

const TASK_CONCURRENCY = 10;

function task_queue_continue(state) {
  var tasks = state.tasks;
  if (tasks.length) {
    if (!state.progress_update_handle) {
      state.progress_update_handle = setInterval(() => {
        state.total_percent = total_percent(tasks);
        var upload_bps = 0;
        var download_bps = 0;
        tasks.forEach(t => {
          if (t.finished) return;
          if (t.type == 'upload') {
            upload_bps += t.current_bps;
          } else {
            download_bps += t.current_bps;
          }
        });
        state.recent_upload_bps.unshift(upload_bps);
        state.recent_upload_bps = state.recent_upload_bps.slice(0, 5);
        state.recent_download_bps.unshift(download_bps);
        state.recent_download_bps = state.recent_download_bps.slice(0, 5);
      }, 1000);
    }
  } else {
    if (state.progress_update_handle) {
      clearInterval(state.progress_update_handle);
      state.progress_update_handle = null;
    }
  }
  var running = tasks.filter(t => t.running);
  var created = tasks.filter(t => t.created);
  if (running.length < TASK_CONCURRENCY) {
    created.slice(0, TASK_CONCURRENCY - running.length).forEach(t => t.start());
  }
}

function average(arr) {
  if (!arr.length) return 0;
  return arr.reduce((a,b) => a + b, 0) / arr.length;
}

export default new Vuex.Store({
  state: {
    client: create_client(),
    space: null,
    space_name: '',
    tasks: [],
    default_passphrase: localStorage.getItem('passphrase') || 'weakencryption',
    pwdata: null, // Password manager data
    notes: null, // notes data
    ingress: null, // ingress
    ingress_data: null, // the private ingress data
    total_percent: 0,
    recent_upload_bps: [],
    recent_download_bps: [],
    progress_update_handle: null,
    favs: new Map(),
    confirm_opts: {},
  },
  getters: {
    token: () => {
      return localStorage.getItem("token");
    },
    authenticated: state => {
      return state.client.authenticated;
    },
    upload_bps: state => {
      return average(state.recent_upload_bps);
    },
    download_bps: state => {
      return average(state.recent_download_bps);
    },
  },
  mutations: {
    set_confirm_opts(state, opts) {
      state.confirm_opts = opts;
    },
    set_space(state, opts) {
      state.space = opts.space;
    },
    upload(state, opts) {
      var space = opts.space;
      var path = opts.path;
      var files = opts.files;
      for (var i = 0; i < files.length; i++) {
        var file = files[i];
        var t = new UploadTask(space, path, file);
        t.on_finish = () => {
          task_queue_continue(state);
        };
        t.on_error = opts.on_error;
        state.tasks.unshift(t);
        process.nextTick(() => task_queue_continue(state));
      }
    },
    download(state, opts) {
      var files = opts.entries;
      var t = new DownloadTask(files);
      t.on_finish = () => {
        task_queue_continue(state);
      };
      t.on_error = opts.on_error;
      state.tasks.unshift(t);
      process.nextTick(() => task_queue_continue(state));
    },
    delete_task(state, opts) {
      var t = opts.task;
      state.tasks = state.tasks.filter(x => x != t);
    },
    delete_succeeded_tasks(state) {
      state.tasks = state.tasks.filter(x => !x.succeeded);
    },
    set_default_passphrase(state, passphrase) {
      localStorage.setItem("passphrase", passphrase);
      state.default_passphrase = passphrase;
    },
    save_token(state, token) {
      localStorage.setItem("token", token);
    },
    logout(state) {
      localStorage.clear();
      state.client.logout();
      location.reload();
    },
    set_pwdata(state, pwdata) {
      state.pwdata = pwdata;
    },
    set_notes(state, notes) {
      state.notes = notes;
    },
    set_favs(state, favs) {
      console.log("set favs", favs);
      state.favs = new Map();
      favs.favs.forEach(x => {
        state.favs.set(x[0], x[1]);
      });
    },
    add_fav(state, file) {
      state.favs.set(file.path, {
        type: file.type,
        metadata: file.metadata,
      });
    },
    del_fav(state, file) {
      state.favs.delete(file.path);
    },
    set_ingress_data(state, data) {
      state.ingress_data = data;
    },
    set_ingress(state, ingress) {
      state.ingress = ingress;
    },
  },
  actions: {
    gen_thumbs(state) {
      var t = new ThumbsTask(state.client, state.space);
      t.start();
    },
    async refresh_userinfo({ state }) {
      await refresh_userinfo(state.client);
    },
    async save_pwdata({ commit, state }, pwdata) {

      var tags = new Set(pwdata.tags);
      pwdata.entries.forEach(e => {
        if (e.tags) {
          e.tags.forEach(t => tags.add(t));
        }
      });
      pwdata.tags = Array.from(tags).sort((x, y) => {
        var a = x.toUpperCase();
        var b = y.toUpperCase();
        return a.localeCompare(b);
      });
      await state.space.write_json_file(pwdata_file_path, pwdata, 'pwdata', true);
      commit('set_pwdata', pwdata);
    },
    async pwdata_create_tag(context, tag) {
      var pwdata = context.state.pwdata;
      if (pwdata.tags.some(t => t == tag)) {
        return;
      }
      pwdata.tags.push(tag);
      return context.dispatch('save_pwdata', pwdata);
    },
    async file_data_init({ state, commit }, { path, initdata, initdata_fn, commit_name, reset, space }) {
      space = space || state.space;
      try {
        if (reset) {
          throw new Error("ENOENT");
        }
        var d = await space.read_json_file(path);
        commit(commit_name, d);
      } catch (e) {
        if (e.message == "ENOENT") {
          if (!initdata) {
            initdata = await initdata_fn();
          }
          await space.write_json_file(path, initdata, null, false);
          commit(commit_name, initdata);
        } else {
          throw e;
        }
      }
    },
    pwdata_init({ dispatch }) {
      return dispatch('file_data_init',
        {
          path: pwdata_file_path,
          initdata: {
            version: 1,
            tags: [],
            entries: [],
          },
          commit_name: 'set_pwdata',
        });
    },
    async delete_tag({ state, dispatch }, tag) {
      var pwdata = state.pwdata;
      pwdata.tags = pwdata.tags.filter(t => t != tag);
      return dispatch('save_pwdata', pwdata);
    },
    notes_init({ dispatch }) {
      return dispatch('file_data_init',
        {
          path: notes_file_path,
          initdata: {
            data: '',
          },
          commit_name: 'set_notes',
        });
    },
    async save_notes({ commit, state }, data) {
      await state.space.write_json_file(notes_file_path, data, 'notes', true);
      commit('set_notes', data);
    },
    async ingress_init({ dispatch, state, commit }) {
      await dispatch('file_data_init',
        {
          path: ingress_file_path,
          initdata_fn: async () => {
            var kp = await state.client.generate_rsa_keypair();
            return {
              public_key: kp.public_key,
              private_key: kp.private_key,
            };
          },
          commit_name: 'set_ingress_data',
        });
      var ingress = await state.client.create_ingress(state.ingress_data.public_key);
      commit("set_ingress", ingress);
    },
    favs_init({ dispatch }, space) {
      return dispatch('file_data_init',
        {
          path: favs_file_path,
          initdata: {
            favs: []
          },
          commit_name: 'set_favs',
          space: space,
        });
    },
    save_favs({ state }) {
      var favs = [];
      state.favs.forEach((v, k) => {
        favs.push([k, v]);
      });
      console.log("save favs", state.favs, favs);
      var data = {
        favs,
      };
      return state.space.write_json_file(favs_file_path, data, 'favs', true);
    },
    add_fav({ commit, dispatch }, file) {
      commit('add_fav', file);
      return dispatch('save_favs');
    },
    del_fav({ commit, dispatch }, file) {
      commit('del_fav', file);
      return dispatch('save_favs');
    },
  },
})
